/**
 * Response Caching System
 * 
 * Provides configurable caching of HTTP responses with:
 * - TTL (time-to-live) based expiration
 * - Manual cache invalidation
 * - Selective caching (only cache GET requests by default)
 * - Cache key generation from URL and method
 * 
 * Usage:
 * ```
 * // Cache GET response for 5 minutes
 * const cached = responseCache.get(url);
 * if (cached) return cached;
 * 
 * const response = await fetch();
 * responseCache.set(url, response, 5 * 60 * 1000);
 * return response;
 * ```
 */

import { HttpResponse } from './http-client';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // milliseconds
}

export interface CacheConfig {
  defaultTTL?: number; // Default 5 minutes
  maxSize?: number; // Maximum number of cache entries
  enableLogging?: boolean;
}

/**
 * Generate cache key from request details
 */
function generateCacheKey(
  method: string,
  url: string,
  body?: unknown,
): string {
  const methodPart = method.toUpperCase();
  const urlPart = url.toLowerCase();

  // For GET requests, just use method + URL
  if (method.toUpperCase() === 'GET') {
    return `${methodPart}:${urlPart}`;
  }

  // For other methods, include body hash
  if (body) {
    const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
    const bodyHash = hashString(bodyStr);
    return `${methodPart}:${urlPart}:${bodyHash}`;
  }

  return `${methodPart}:${urlPart}`;
}

/**
 * Simple string hash function
 */
function hashString(str: string): string {
  let hash = 0;
  if (str.length === 0) return '0';

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
}

/**
 * Response Cache Manager
 */
class ResponseCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private defaultTTL: number;
  private maxSize: number;
  private enableLogging: boolean;

  constructor(config: CacheConfig = {}) {
    this.defaultTTL = config.defaultTTL || 5 * 60 * 1000; // 5 minutes default
    this.maxSize = config.maxSize || 100;
    this.enableLogging = config.enableLogging || false;
  }

  /**
   * Check if a cached entry exists and is still valid
   */
  get<T>(method: string, url: string, body?: unknown): T | null {
    const key = generateCacheKey(method, url, body);
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      this.log(`Cache MISS: ${key}`);
      return null;
    }

    // Check if cache entry has expired
    const age = Date.now() - entry.timestamp;
    if (age > entry.ttl) {
      this.log(`Cache EXPIRED: ${key} (age: ${age}ms, ttl: ${entry.ttl}ms)`);
      this.cache.delete(key);
      return null;
    }

    this.log(`Cache HIT: ${key} (age: ${age}ms)`);
    return entry.data;
  }

  /**
   * Store a response in cache
   */
  set<T>(
    method: string,
    url: string,
    data: T,
    ttl?: number,
    body?: unknown,
  ): void {
    const key = generateCacheKey(method, url, body);
    const finalTTL = ttl ?? this.defaultTTL;

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value as string | undefined;
      if (firstKey) {
        this.cache.delete(firstKey);
        this.log(`Cache EVICTED (LRU): ${firstKey}`);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: finalTTL,
    });

    this.log(`Cache SET: ${key} (ttl: ${finalTTL}ms)`);
  }

  /**
   * Invalidate a specific cache entry
   */
  invalidate(method: string, url: string, body?: unknown): boolean {
    const key = generateCacheKey(method, url, body);
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.log(`Cache INVALIDATED: ${key}`);
    }
    return deleted;
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: string | RegExp): number {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let count = 0;

    const keysToDelete: string[] = [];
    Array.from(this.cache.keys()).forEach((key) => {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
      count++;
      this.log(`Cache INVALIDATED (pattern): ${key}`);
    });

    return count;
  }

  /**
   * Clear all cache
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    this.log(`Cache CLEARED (${size} entries removed)`);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      utilization: `${((this.cache.size / this.maxSize) * 100).toFixed(1)}%`,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => {
        const age = Date.now() - entry.timestamp;
        return {
          key,
          age: `${age}ms`,
          ttl: `${entry.ttl}ms`,
          expired: age > entry.ttl,
        };
      }),
    };
  }

  /**
   * Internal logging
   */
  private log(message: string): void {
    if (this.enableLogging) {
      console.log(`[ResponseCache] ${message}`);
    }
  }
}

export const responseCache = new ResponseCache({
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxSize: 100,
  enableLogging: false, // Set to true for debugging
});

// Export both instance and class for testing
export { ResponseCache };
